<template>
  <!--BOOST LANDING PAGE: Story CD-18 -->
  <Card
    class="w-full max-w-[510px] min-h-[545px] overflow-hidden shadow-md rounded-xl h-full"
    :class="`${cardClasses} ${isMobileOrIsTable ? 'min-h-[343px]' : ''}`"
  >
    <CardContent class="p-0 flex flex-col h-full" :class="contentClasses">
      <div class="p-6 flex-1">
        <div class="mb-4 items-start" :class="{ 'flex justify-between items-center': isMobileOrIsTable }">
          <div>
            <p class="text-[16px] font-semibold" :class="titleClasses">{{ currentOrder?.merchantName }}</p>
            <p class="text-[14px] font-small" :class="titleClasses" v-if="isPayAtEnabled">
              Pay@ Ref: <span class="font-medium">{{ paymentPlan?.payAtReferenceNumber }}</span>
            </p>
            <p class="text-[14px] font-small" :class="titleClasses">{{ currentOrder?.reference }}</p>
          </div>
          <img :src="BoostIcon" :class="isMobileOrIsTable ? 'h-[24px] w-[70px]' : 'h-[49px] w-[144px]'" />
        </div>

        <div class="relative mb-3">
          <img
            :src="currentOrder?.imageUrl || DefaultBoostLogo"
            alt="Boost"
            class="w-full rounded-[16px] object-contain"
            :class="isMobileOrIsTable ? 'h-[150px]' : 'h-[229px]'"
          />
        </div>

        <div v-if="!isRedeemed">
          <div class="mb-3 flex items-end justify-between">
            <div>
              <p class="text-[14px] text-grey-light-600">Goal</p>
              <p class="text-[20px] font-semibold text-brand-700">
                {{ displayCurrency(orderTotal) }}
              </p>
            </div>
            <div class="text-right">
              <p class="text-[14px] text-grey-light-600">Boost Rewards</p>
              <p class="text-[20px] font-semibold text-brand-700">
                {{ displayCurrency(boostRewardAmount) }}
              </p>
            </div>
          </div>

          <div class="mb-3">
            <div class="flex gap-0 mb-0">
              <div class="relative h-2 w-full overflow-hidden rounded-l-full bg-grey-light-200">
                <div class="h-full transition-all" :class="progressBarClasses" :style="{ width: `${progress}%` }" />
              </div>
              <img :src="ComputedTrophyIcon" class="relative top-[-8px] left-[-.5px]" />
            </div>

            <div class="mt-[-2px] flex items-center justify-between text-xs">
              <span v-if="isCancelled || isExpired" class="h-[22px]"> </span>
              <span
                v-else
                class="flex items-center py-[3px] px-[6px] gap-1"
                :class="{
                  'text-success-700 bg-success-50 border-[1px] border-success-200 rounded-[6px] h-[22px] truncate':
                    reachedThreshold || isCompleted,
                }"
              >
                <span v-if="reachedThreshold" class="flex items-center py-[3px] px-[6px] gap-1">
                  <Check class="h-3 w-3" v-if="!isCompleted" />
                  {{ 'Minimum savings threshold reached' }}
                </span>
                <span v-else-if="isCompleted">Done!</span>
              </span>
              <span class="font-medium text-[14px] text-grey-light-700">{{ progress }}%</span>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-auto">
        <hr />
        <div class="flex gap-2 justify-between px-6 py-4 h-[73px]">
          <Button
            v-if="!isRedeemed"
            variant="outline"
            size="sm"
            class="w-[66px] h-[40px] font-semibold"
            :class="{ 'border-boost-500 hover:bg-boost-600 text-boost-600': isCompleted }"
            @click="navigateToDetails"
          >
            View
          </Button>

          <div class="flex justify-center w-full" v-if="isRedeemed">
            <div
              size="sm"
              class="flex items-center justify-center gap-2 border-[1px] border-boost-500 text-boost-500 bg-boost-25 hover:bg-boost-25 w-[180px] h-[40px] rounded-lg"
            >
              <Check class="h-3 w-3" />
              Voucher Redeemed
            </div>
          </div>
          <div
            v-else-if="isCancelled || isExpired"
            size="sm"
            class="bg-error-50 text-error-700 hover:bg-error-50 min-w-[100px] border-error-200 border-[1px] h-[40px] rounded-[9999px] flex justify-center items-center px-2"
          >
            {{ isCancelled ? 'Cancelled' : 'Expired' }}
          </div>

          <Button
            v-else
            size="sm"
            class="bg-brand-600 hover:bg-brand-700 w-[132px] h-[40px] font-semibold"
            @click="showPaymentModal = true"
            :disabled="isExpired || isCancelling || isCompleted"
          >
            Add Savings
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>

  <Dialog :open="showPaymentModal" @update:open="onDialogUpdate">
    <DialogContent :class="dialogWidthClass" preventClose>
      <ContributeModal
        v-if="currentOrder"
        :order="currentOrder"
        :upcoming="upcoming"
        @close="onContributeClose"
        @completed="onPaymentCompleted"
        @step-change="contributeStep = $event"
      />
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import DefaultBoostLogo from '@/assets/boost-powered-by.svg';
import BoostActiveIcon from '@/assets/shopper/active-boost.svg';
import BoostCancelledIcon from '@/assets/shopper/cancel-boost.svg';
import CompleteTrophyIcon from '@/assets/shopper/complete-trophy.svg';
import BoostCompletedIcon from '@/assets/shopper/completed-boost.svg';
import TrophyIcon from '@/assets/shopper/done.svg';
import ContributeModal from '@/components/shopper/contribute/ContributeModal.vue';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { getOrderByIdWithPlansAndPayments } from '@/services/orders';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException } from '@/utils/monitoring.utils';
import { upcomingPayments } from '@/utils/paymentPlan.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { Check } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { ModelOrderResponse } from '@layup/api-sdk';

interface Props {
  order?: ModelOrderResponse;
}

const props = defineProps<Props>();
const emit = defineEmits<{ 'payment-completed': [] }>();
const router = useRouter();
const showPaymentModal = ref(false);
const contributeStep = ref<string>('');

const localOrder = ref<ModelOrderResponse | undefined>(undefined);
const currentOrder = computed<ModelOrderResponse | undefined>(() => localOrder.value ?? props.order);

const dialogWidthClass = computed(() => [
  contributeStep.value === 'payment'
    ? // Full-screen on mobile for the payment step; revert to a centered card on sm+.
      'inset-0 left-0 top-0 translate-x-0 translate-y-0 w-screen h-[100dvh] max-w-none max-h-none rounded-none p-4 overflow-y-auto sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:h-auto sm:w-full sm:max-h-[90vh] sm:rounded-xl sm:p-6 transition-all duration-300'
    : 'w-[calc(100vw-1rem)] sm:w-full max-h-[90vh] overflow-y-auto transition-all duration-300 rounded-xl p-3 sm:p-6',
  contributeStep.value === 'debi-check'
    ? 'sm:max-w-[640px]'
    : contributeStep.value === 'select-method' || contributeStep.value === 'status'
      ? 'sm:max-w-[560px]'
      : 'sm:max-w-[420px]',
]);
const { isMobile, isTablet } = useBreakpoint();

const isMobileOrIsTable = computed(() => isMobile.value || isTablet.value);
const isCompleted = computed(() => currentOrder.value?.state === 'COMPLETED');
const isCancelled = computed(() => currentOrder.value?.state === 'CANCELLED');
const isCancelling = computed(() => currentOrder.value?.state === 'CANCELLING');
const isRedeemed = computed(() => currentOrder.value?.voucher?.state === 'CREATED');
const isExpired = computed(() => currentOrder.value?.state === 'EXPIRED');
const isPartial = computed(() => currentOrder.value?.state === 'PARTIAL');

const planValue = computed(() => {
  const products = currentOrder.value?.products ?? [];
  const savingsGoal = products
    .filter((p: any) => p.sku !== 'BOOST-REWARD')
    .reduce((sum: number, p: any) => sum + (p.amount ?? 0), 0);
  return savingsGoal || (currentOrder.value?.amountDue ?? 0);
});

const orderBalance = computed(() => currentOrder.value?.balance ?? 0);
const orderTotal = computed(() => planValue.value);

const cardClasses = computed(() => {
  if (isCompleted.value || isRedeemed.value) {
    return 'border-[1px] border-boost-500 bg-boost-25';
  }

  if (isCancelled.value || isExpired.value) {
    return 'border-[1px] border-grey-light-200 shadow-grey-light-200 opacity-50';
  }

  return 'border-[1px] border-grey-light-200 shadow-grey-light-200';
});

const contentClasses = computed(() => {
  return isCancelled.value || isExpired.value ? 'opacity-80' : '';
});

const titleClasses = computed(() => {
  if (isCompleted.value || isRedeemed.value) {
    return 'text-boost-500';
  }

  return 'text-grey-light-900';
});

const progressBarClasses = computed(() => {
  if (isCompleted.value || isRedeemed.value) {
    return 'bg-boost-500';
  }

  if (isCancelled.value || isExpired.value) {
    return 'bg-grey-light-300';
  }

  return 'bg-teal-600';
});

const progress = computed(() => {
  if (!orderTotal.value || isPartial.value || isExpired.value) return 0;

  return Math.round(((orderTotal.value - orderBalance.value) / orderTotal.value) * 100);
});

const paidAmountInCents = computed(() => {
  if (orderTotal.value === 0) return 0;

  return orderTotal.value - orderBalance.value;
});

const BoostIcon = computed(() => {
  if (isCompleted.value) {
    return BoostCompletedIcon;
  }

  if (isCancelled.value || isExpired.value) {
    return BoostCancelledIcon;
  }

  return BoostActiveIcon;
});

const ComputedTrophyIcon = computed(() => {
  if (isCompleted.value) {
    return CompleteTrophyIcon;
  }

  return TrophyIcon;
});

const upcoming = computed(() => {
  const plan = currentOrder.value?.plans?.[0];
  return plan ? upcomingPayments(plan as any) : [];
});

const reachedThreshold = computed(
  () => paidAmountInCents.value >= currentOrder.value?.voucherSettings?.minimumSavingGoalInCents!,
);

const boostRewardAmount = computed(() => {
  const amount =
    currentOrder.value?.voucherSettings?.rewardType === 'PERCENTAGE'
      ? (currentOrder.value?.voucherSettings.rewardPercentage! / 100) * planValue.value
      : currentOrder.value?.voucherSettings?.rewardFlatAmountInCents;

  return amount;
});

const paymentPlan = computed(() => currentOrder.value?.plans?.[0]);

const isPayAtEnabled = computed(() => currentOrder.value?.paymentMethods?.payAt!);

const onContributeClose = () => {
  showPaymentModal.value = false;
  if (contributeStep.value === 'status') {
    onPaymentCompleted();
  }
};

const onDialogUpdate = (open: boolean) => {
  if (!open) {
    onContributeClose();
  }
};

const onPaymentCompleted = async () => {
  showPaymentModal.value = false;
  const id = props.order?._id;
  if (id) {
    try {
      localOrder.value = await getOrderByIdWithPlansAndPayments(id);
      addMetric('contribute_completed', { channel: 'shopper', orderId: id, orderType: 'boost' });
    } catch (error) {
      createException(error);
    }
  }
  emit('payment-completed');
};

const navigateToDetails = () => {
  router.push({ name: 'shopperBoostRewardDetails', params: { id: currentOrder.value?._id } });
};
</script>
